/**
 * 跟語系相關的 Composable
 *
 * @returns {
 *   isCN: 是否為中國語系,
 *   isJP: 是否為日本語系,
 *   isEU: 是否為歐洲語系,
 *   isKR: 是否為韓國語系,
 *   isTW: 是否為台灣語系,
 *   provideLocalePath: 根據語系 ID 取得對應的路徑
 *   provideRegionName: 根據區域代碼取得對應的區域名稱,
 *   provideLocaleName: 根據語系代碼取得對應的語系名稱
 *   provideRegionGroupName: 根據區域群組代碼取得對應的區域群組名稱,
 *   isShowLocaleButton: 是否顯示語系選擇按鈕
 *   isSupportRegionLocale: 根據 regionCode 判斷目前的 locale 是否支援該區域的語系
 *   currentRegion: 根據目前的 locale 取得對應的 regionCode
 *   contextRegions: 根據目前登入帳號的 RBU，過濾出可用的區域清單
 *   groupingRegions: 將區域清單分組
 * }
 */

import type { IRegion } from '@/assets/types/api/Common';

interface IRegionMenu {
  regionGroupCode: string;
  regions: IRegion[];
}

export function useLanguage() {
  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT } = runtimeConfig.public;

  const route = useRoute();
  const error = useError();
  const switchLocalePath = useSwitchLocalePath();

  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;

  const mainStore = useMainStore();
  const profileStore = useProfileStore();
  const { regions, regionCode } = storeToRefs(mainStore);
  const { isLogin, profile } = storeToRefs(profileStore);

  const { validateSupportRegionLocale } = validateRegionLocale();

  const isCN = computed((): boolean => locale.value === EnumLocale.ZH_CN);
  const isEU = computed((): boolean => locale.value === EnumLocale.EN_EMEA);
  const isKR = computed((): boolean => locale.value === EnumLocale.EN_KR);
  const isJP = computed(
    (): boolean =>
      locale.value === EnumLocale.JA_JP || locale.value === EnumLocale.EN_JP
  );
  const isTW = computed(
    (): boolean =>
      locale.value === EnumLocale.ZH_TW || locale.value === EnumLocale.EN_TW
  );

  // 回傳語系名稱
  const provideLocaleName = (code: string | EnumLocale): string => {
    const localeCode = code ? code.toLocaleLowerCase() : '';

    switch (localeCode) {
      case EnumLocale.ZH_TW:
        return t('region.zh-tw');
      case EnumLocale.ZH_CN:
        return t('region.zh-cn');
      case EnumLocale.JA_JP:
        return t('region.ja-jp');
      case EnumLocale.EN_INT:
      case EnumLocale.EN_AMERICAS:
      case EnumLocale.EN_EMEA:
      case EnumLocale.EN_JP:
      case EnumLocale.EN_TW:
      case EnumLocale.EN_KR:
      case EnumLocale.EN_ASIA_PACIFIC:
        return t('region.english');
      default:
        return localeCode;
    }
  };

  // 回傳區域名稱
  const provideRegionName = (code: string | EnumRegion): string => {
    const regionCode = code ? code.toLocaleLowerCase() : '';

    switch (regionCode) {
      case EnumRegion.AMERICAS:
        return t('region.americas');
      case EnumRegion.EMEA:
        return t('region.emea');
      case EnumRegion.CN:
        return t('region.china');
      case EnumRegion.JP:
        return t('region.japan');
      case EnumRegion.KR:
        return t('region.korea');
      case EnumRegion.TW:
        return t('region.taiwan');
      case EnumRegion.ASIA_PACIFIC:
        return t('region.asia-pacific');
      case EnumRegion.INT:
        return t('region.int');
      default:
        return regionCode;
    }
  };

  // 回傳區域群組名稱
  const provideRegionGroupName = (code: string): string => {
    return code === 'asia-pacific-group' ? t('region.asia-pacific') : code;
  };

  // 語系選擇按鈕的連結
  const provideLocalePath = (id: EnumLocale): string => {
    if (id === EnumLocale.ZH_CN || error.value) {
      const domain = provideDomain(id, ENVIRONMENT);
      return `${domain}/${id}/marketplace`;
    }
    return switchLocalePath(id);
  };

  // 是否顯示語系選擇按鈕
  const isShowLocaleButton = computed((): boolean => {
    if (isCN.value) {
      return false;
    }

    if (isLogin.value) {
      if (
        regionCode.value === EnumRegion.TW ||
        regionCode.value === EnumRegion.JP
      ) {
        return true;
      }
      return false;
    }
    return true;
  });

  // 根據 regionCode 判斷目前的 locale 是否支援該區域的語系
  const isSupportRegionLocale = computed((): boolean => {
    const payload = {
      path: route.path,
      locale: locale.value,
      regions: regions.value,
      regionCode: regionCode.value,
    };
    return validateSupportRegionLocale(payload);
  });

  // 根據目前的 locale 取得對應的 regionCode
  const currentRegion = computed((): EnumRegion | null => {
    return regions.value.reduce((prev: EnumRegion | null, current: IRegion) => {
      const { regionCode, websites } = current;
      if (prev !== null) {
        return prev;
      }
      if (websites.some((item) => item.code === locale.value)) {
        return regionCode;
      }
      return null;
    }, null);
  });

  // 根據目前登入帳號的 RBU，過濾出可用的區域清單
  const contextRegions = computed((): IRegion[] => {
    if (isLogin.value) {
      return regions.value.filter(
        (item) => item.regionCode === profile.value.regionCode
      );
    }
    return regions.value;
  });

  // 將區域清單分組
  const groupingRegions = computed((): (IRegion | IRegionMenu)[] => {
    return contextRegions.value.reduce(
      (prev: (IRegion | IRegionMenu)[], current: IRegion) => {
        const result = [...prev];
        const { regionGroupCode } = current;

        if (regionGroupCode === null) {
          result.push(current);
          return result;
        }

        if (result.length) {
          const index = result.length - 1;
          const prevItem = result[index] as IRegionMenu;

          if (
            prevItem.regionGroupCode &&
            prevItem.regionGroupCode === regionGroupCode
          ) {
            (result[index] as IRegionMenu).regions.push(current);
            return result;
          }
        }

        const group = { regionGroupCode, regions: [current] };
        result.push(group);
        return result;
      },
      [] as (IRegion | IRegionMenu)[]
    );
  });

  return {
    isCN,
    isJP,
    isEU,
    isKR,
    isTW,
    isShowLocaleButton,
    isSupportRegionLocale,
    provideLocalePath,
    provideRegionName,
    provideLocaleName,
    provideRegionGroupName,
    currentRegion,
    contextRegions,
    groupingRegions,
  };
}

export type { IRegionMenu };
